import { Logger as SeqLogger } from 'seq-logging/browser';
import { getEnv } from '../config/env';

export type LogLevelName = 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace';

const levelOrder: LogLevelName[] = ['fatal', 'error', 'warn', 'info', 'debug', 'trace'];
const levelRank: Record<LogLevelName, number> = levelOrder.reduce(
  (acc, l, i) => {
    acc[l] = i;
    return acc;
  },
  {} as Record<LogLevelName, number>
);

// Map our log levels to Seq log levels
const seqLevelMap: Record<LogLevelName, string> = {
  fatal: 'Fatal',
  error: 'Error',
  warn: 'Warning',
  info: 'Information',
  debug: 'Debug',
  trace: 'Verbose'
};

interface LogMeta {
  [k: string]: unknown;
}

function nowIso() {
  return new Date().toISOString();
}

function safeSerialize(meta?: LogMeta): Record<string, unknown> | undefined {
  if (!meta) return undefined;
  try {
    return JSON.parse(JSON.stringify(meta));
  } catch {
    return { note: 'meta_unserializable' };
  }
}

// Global logger properties for context (user, org, etc.)
let globalProperties: Record<string, unknown> = {
  ApplicationName: '',
  Environment: '',
  UserName: 'Not yet logged in',
  UserEmail: 'Not yet logged in',
  Organization: 'Not yet logged in',
  OrganizationId: 'Not yet logged in'
};

class Logger {
  private currentLevel: LogLevelName;
  private seqLogger: SeqLogger | null = null;
  private threshold: number = 0;

  constructor() {
    let configured: LogLevelName = 'info';
    let threshold = 0;
    try {
      const env = getEnv();
      configured = env.VITE_LOG_LEVEL;
      threshold = env['VITE_SEQ_LOGGING_THRESHOLD'];
      this.threshold = threshold;
      this.initSeqLogger(env);
    } catch {
      /* ignore until env ready */
    }
    this.currentLevel = configured;
  }

  private initSeqLogger(env: ReturnType<typeof getEnv>) {
    if (!env['VITE_SEQ_URL']) {
      // Seq is optional — only log to console
      return;
    }

    try {
      globalProperties['ApplicationName'] = env['VITE_SEQ_APPLICATION_NAME'];
      globalProperties['Environment'] = env['VITE_SEQ_ENVIRONMENT'];

      this.seqLogger = new SeqLogger({
        serverUrl: env['VITE_SEQ_URL'],
        apiKey: env['VITE_SEQ_API_KEY'],
        onError: (error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Seq logging error:', error);
        },
        batchSizeLimit: 10,
        batchInterval: 1000,
        onBatchError: (error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Seq batch error:', error);
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize Seq logger:', err);
      this.seqLogger = null;
    }
  }

  setLevel(level: LogLevelName) {
    this.currentLevel = level;
  }

  private enabled(level: LogLevelName) {
    return levelRank[level] <= levelRank[this.currentLevel];
  }

  private emit(level: LogLevelName, message: string, meta?: LogMeta) {
    if (!this.enabled(level)) return;

    const mergedMeta = { ...globalProperties, ...safeSerialize(meta) };
    const payload = { ts: nowIso(), level, msg: message, ...mergedMeta };

    // Always log to console
    const method: keyof Console =
      level === 'fatal' || level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
    // eslint-disable-next-line no-console
    console[method](payload);

    // Also send to Seq if configured and threshold allows
    if (this.seqLogger && levelRank[level] <= this.threshold) {
      try {
        this.seqLogger.emit({
          timestamp: new Date(),
          level: seqLevelMap[level],
          messageTemplate: message,
          properties: mergedMeta
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to emit to Seq:', err);
      }
    }
  }

  private emitError(message: string, error: Error | unknown, level: LogLevelName) {
    if (!this.enabled(level)) return;

    const errorProperties = error instanceof Error ? {
      ErrorType: error.name || 'Unknown',
      ErrorMessage: error.message,
      ErrorStackTrace: error.stack
    } : {
      ErrorType: 'Unknown',
      ErrorMessage: String(error)
    };

    const mergedMeta = { ...globalProperties, ...errorProperties };
    const payload = { ts: nowIso(), level, msg: message, ...mergedMeta };

    // Always log to console
    const method: keyof Console = level === 'fatal' || level === 'error' ? 'error' : 'warn';
    // eslint-disable-next-line no-console
    console[method](payload);

    // Also send to Seq if configured and threshold allows
    if (this.seqLogger && levelRank[level] <= this.threshold) {
      try {
        this.seqLogger.emit({
          timestamp: new Date(),
          level: seqLevelMap[level],
          messageTemplate: message,
          properties: mergedMeta
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to emit error to Seq:', err);
      }
    }
  }

  fatal(message: string, meta?: LogMeta) {
    this.emit('fatal', message, meta);
  }

  error(message: string, error?: Error | LogMeta) {
    if (error instanceof Error) {
      this.emitError(message, error, 'error');
    } else {
      this.emit('error', message, error);
    }
  }

  warn(message: string, meta?: LogMeta) {
    this.emit('warn', message, meta);
  }

  info(message: string, meta?: LogMeta) {
    this.emit('info', message, meta);
  }

  debug(message: string, meta?: LogMeta) {
    this.emit('debug', message, meta);
  }

  trace(message: string, meta?: LogMeta) {
    this.emit('trace', message, meta);
  }

  start(scope: string, meta?: LogMeta) {
    this.trace(`START ${scope} ...`, meta);
  }

  end(scope: string, meta?: LogMeta) {
    this.trace(`END ${scope}`, meta);
  }

  redact(value: unknown): string {
    if (value == null) return 'null';
    if (typeof value === 'string') return `len:${value.length}`;
    if (typeof value === 'object')
      return `keys:${Object.keys(value as Record<string, unknown>).length}`;
    return typeof value;
  }

  /**
   * Set global logger context properties (user, organization, etc.).
   * These are automatically attached to all log messages.
   */
  setProperties(userName: string, userEmail: string, organization: string, organizationId: string) {
    globalProperties['UserName'] = userName;
    globalProperties['UserEmail'] = userEmail;
    globalProperties['Organization'] = organization;
    globalProperties['OrganizationId'] = organizationId;
  }

  /**
   * Clear global logger context properties when user logs out.
   */
  clearProperties() {
    globalProperties['UserName'] = 'Not yet logged in';
    globalProperties['UserEmail'] = 'Not yet logged in';
    globalProperties['Organization'] = 'Not yet logged in';
    globalProperties['OrganizationId'] = 'Not yet logged in';
  }
}

export const logger = new Logger();
